/**
 * Shared formatting utilities for currency, percentages, and changes.
 *
 * Centralizes formatting logic used across StatisticsTab, DashboardPage,
 * and other components that display financial/metric data.
 */

/**
 * Format a number as a compact currency string.
 *
 * @param value - The numeric value to format
 * @returns Formatted string like "$1.2M", "$150K", or "$42"
 */
export function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

/**
 * Format a number as a full currency string with commas.
 *
 * @param value - The numeric value to format
 * @returns Formatted string like "$1,234,567.00"
 */
export function formatCurrencyFull(value: number): string {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Format a percentage change value with sign indicator.
 *
 * @param value - The percentage value (e.g., 12.5 for +12.5%)
 * @returns Formatted string like "+12.5%" or "-3.2%" or "N/A"
 */
export function formatPercentage(value: number | null | undefined): string {
  if (value == null) return 'N/A';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

/**
 * Determine if a change value is positive, negative, or neutral.
 * Supports "inverted" metrics where lower is better (e.g., response time).
 *
 * @param value - The change value
 * @param inverted - If true, negative change is "positive" (good)
 * @returns 'positive' | 'negative' | 'neutral' | null
 */
export function getChangeDirection(
  value: number | null | undefined,
  inverted = false,
): 'positive' | 'negative' | 'neutral' | null {
  if (value == null) return null;
  const isPositive = inverted ? value < 0 : value > 0;
  const isNegative = inverted ? value > 0 : value < 0;
  if (isPositive) return 'positive';
  if (isNegative) return 'negative';
  return 'neutral';
}

/**
 * Get an arrow character for a change value.
 *
 * @param value - The change value
 * @returns '\u2191' (up), '\u2193' (down), or '' (no change/null)
 */
export function getChangeArrow(value: number | null | undefined): string {
  if (value == null) return '';
  if (value > 0) return '\u2191';
  if (value < 0) return '\u2193';
  return '';
}

/**
 * Map a 0-1 confidence score to a semantic level.
 *
 * @param confidence - Score between 0 and 1
 * @returns 'high' | 'medium' | 'low'
 */
export function getConfidenceLevel(
  confidence: number,
): 'high' | 'medium' | 'low' {
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.5) return 'medium';
  return 'low';
}

/**
 * Map a 0-1 confidence score to a CSS color variable.
 *
 * @param confidence - Score between 0 and 1
 * @returns CSS color string (green, orange, or red)
 */
export function getConfidenceColor(confidence: number): string {
  if (confidence >= 0.8) return 'var(--color-success, #16a34a)';
  if (confidence >= 0.5) return 'var(--color-warning, #f59e0b)';
  return 'var(--color-error, #dc2626)';
}

/**
 * Format a monetary value (string or number) as full USD with commas.
 *
 * Handles null, undefined, and unparseable strings gracefully.
 * Previously duplicated in Customer360Page, PortalCustomerDetailPage,
 * PortalCustomersPage, and OrdersPage.
 *
 * @param val - Monetary value (string from API or number)
 * @returns Formatted string like "$1,234.56" or "$0.00"
 */
export function formatMoney(
  val: string | number | null | undefined,
  nullDisplay: string = '$0.00',
): string {
  if (val == null) return nullDisplay;
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return nullDisplay;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Calculate the total amount owed from AR aging buckets.
 *
 * Sum of current_balance (0-30 days) + aging categories 1-4.
 * Previously duplicated in Customer360Page and PortalCustomerDetailPage.
 *
 * @param ar - AR aging object with balance fields
 * @returns Total owed as a number
 */
/**
 * Parse a weight value (string or number) into a number, or null if unparseable.
 *
 * Product ship_weight is stored as a string in Sage (e.g. "25", "25.5", "");
 * empty strings and whitespace-only values resolve to null.
 */
export function parseWeight(
  val: string | number | null | undefined,
): number | null {
  if (val == null) return null;
  if (typeof val === 'number') return isNaN(val) ? null : val;
  const trimmed = val.trim();
  if (trimmed === '') return null;
  const num = parseFloat(trimmed);
  return isNaN(num) ? null : num;
}

/**
 * Format a unit weight for display (e.g. "25 lbs", or "—" when missing).
 *
 * Used in the portal order entry UI so staff can estimate how much product
 * to add when a customer asks to "fill with..." a specific amount.
 */
export function formatWeight(
  val: string | number | null | undefined,
  nullDisplay: string = '—',
): string {
  const num = parseWeight(val);
  if (num === null) return nullDisplay;
  // Preserve up to 2 decimals but drop trailing zeros for readability.
  const str = num % 1 === 0 ? num.toFixed(0) : num.toFixed(2).replace(/\.?0+$/, '');
  return `${str} lbs`;
}

/**
 * Pick the authoritative per-unit ship weight for a product-shaped row.
 *
 * Sage stores two weight fields per item:
 *   - ``udf_ship_wgt_lbs`` (user-defined) — what the Sage freight
 *     estimate API reads. This is the "truth" the sales-site freight
 *     quote is built from, so it is what portal totals must use.
 *   - ``ship_weight`` — standard Sage field; historically incorrect
 *     for some SKUs, but always present on older rows.
 *
 * Always prefer ``udf_ship_wgt_lbs`` when it parses to a number; fall
 * back to ``ship_weight`` so rows synced before the UDF was populated
 * still display a weight.
 */
export function resolveShipWeight(
  row:
    | {
        udf_ship_wgt_lbs?: string | number | null;
        ship_weight?: string | number | null;
      }
    | null
    | undefined,
): string | number | null {
  if (!row) return null;
  if (parseWeight(row.udf_ship_wgt_lbs) !== null) {
    return row.udf_ship_wgt_lbs ?? null;
  }
  return row.ship_weight ?? null;
}

/**
 * Compute the total weight of a line (quantity * unit weight), or null when
 * either side is missing/unparseable.
 */
export function calculateLineWeight(
  quantity: string | number | null | undefined,
  unitWeight: string | number | null | undefined,
): number | null {
  const q = parseWeight(quantity);
  const w = parseWeight(unitWeight);
  if (q === null || w === null) return null;
  return q * w;
}

/**
 * Given a target total weight and a per-unit weight, compute the number
 * of whole units needed to fill that target. Used by the "Fill with this
 * product" flow when a customer says "fill with 1000 lbs of X". Returns
 * null if either input is missing/non-positive; returns at least 1 unit
 * when both inputs are valid.
 */
export function quantityForTargetWeight(
  targetWeight: number | string | null | undefined,
  unitWeight: number | string | null | undefined,
): number | null {
  const target = parseWeight(targetWeight);
  const unit = parseWeight(unitWeight);
  if (target === null || unit === null) return null;
  if (target <= 0 || unit <= 0) return null;
  return Math.max(1, Math.round(target / unit));
}

export function totalOwed(ar: {
  current_balance?: string | number | null;
  aging_category1?: string | number | null;
  aging_category2?: string | number | null;
  aging_category3?: string | number | null;
  aging_category4?: string | number | null;
}): number {
  const parse = (v: string | number | null | undefined) => {
    const n = typeof v === 'string' ? parseFloat(v) : (v ?? 0);
    return isNaN(n as number) ? 0 : (n as number);
  };
  return parse(ar.current_balance) + parse(ar.aging_category1)
    + parse(ar.aging_category2) + parse(ar.aging_category3)
    + parse(ar.aging_category4);
}
